// Mirrors the EAP prototype's setCtaColour(): a client supplies one CTA colour and every
// primary-family token (fills, tints, borders, glows, gradients, text roles) derives from it.

// [red, green, blue]. Not a tuple type: the pinned @typescript-eslint/parser@2 can't
// parse TS 5 tuples.
type Rgb = number[];

export type ColourMode = "light" | "dark";

export type CtaTokens = Record<string, string>;

const WHITE: Rgb = [255, 255, 255];
const BLACK: Rgb = [0, 0, 0];
const INK: Rgb = [15, 17, 35];
const DARK_SURFACE: Rgb = [21, 24, 39];
const MINIMUM_TEXT_CONTRAST = 4.5;
const MINIMUM_FILL_CONTRAST = 3;

const clampChannel = (value: number): number =>
  Math.round(Math.max(0, Math.min(255, value)));

const toHex = (colour: Rgb): string =>
  `#${colour
    .map((channel) =>
      clampChannel(channel)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;

const toChannelList = (colour: Rgb): string =>
  colour.map(clampChannel).join(" ");

const mix = (from: Rgb, to: Rgb, amount: number): Rgb =>
  from.map((channel, index) => channel + (to[index] - channel) * amount);

const toHsl = ([red, green, blue]: Rgb): Rgb => {
  const [r, g, b] = [red / 255, green / 255, blue / 255];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;
  const delta = max - min;
  if (!delta) return [0, 0, lightness];
  const saturation = delta / (1 - Math.abs(2 * lightness - 1));
  const hueSector =
    max === r
      ? ((g - b) / delta) % 6
      : max === g
      ? (b - r) / delta + 2
      : (r - g) / delta + 4;
  return [(hueSector * 60 + 360) % 360, saturation, lightness];
};

const fromHsl = ([hue, saturation, lightness]: Rgb): Rgb => {
  const offset = (step: number): number => (step + hue / 30) % 12;
  const chroma = saturation * Math.min(lightness, 1 - lightness);
  return [0, 8, 4].map(
    (step) =>
      255 *
      (lightness -
        chroma * Math.max(-1, Math.min(offset(step) - 3, 9 - offset(step), 1))),
  );
};

const relativeLuminance = (colour: Rgb): number => {
  const [red, green, blue] = colour.map((channel) => {
    const normalised = channel / 255;
    return normalised <= 0.03928
      ? normalised / 12.92
      : ((normalised + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
};

export const contrastRatio = (first: Rgb, second: Rgb): number => {
  const [lighter, darker] = [
    relativeLuminance(first),
    relativeLuminance(second),
  ].sort((a, b) => b - a);
  return (lighter + 0.05) / (darker + 0.05);
};

export const parseColour = (value: string | null | undefined): Rgb | null => {
  const trimmed = value?.trim().toLowerCase() ?? "";
  const shortHex = trimmed.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/);
  if (shortHex) {
    return shortHex.slice(1).map((digit) => parseInt(digit + digit, 16));
  }
  const longHex = trimmed.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/);
  if (longHex) return longHex.slice(1).map((pair) => parseInt(pair, 16));
  const rgbFunction = trimmed.match(/^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/);
  if (rgbFunction) return rgbFunction.slice(1).map(Number);
  return null;
};

// Text on a primary fill: white unless the CTA colour is too light for it.
const onPrimaryText = (primary: Rgb): string =>
  contrastRatio(primary, WHITE) >= MINIMUM_FILL_CONTRAST ? "#fff" : toHex(INK);

const deriveLightTokens = (primary: Rgb): CtaTokens => {
  const [hue, saturation, lightness] = toHsl(primary);
  const accent = fromHsl([
    (hue + 12) % 360,
    saturation * 0.92,
    Math.min(lightness + 0.03, 0.8),
  ]);
  const deep = mix(primary, BLACK, 0.45);
  const readableDeep =
    contrastRatio(deep, WHITE) >= MINIMUM_TEXT_CONTRAST
      ? deep
      : mix(primary, INK, 0.7);
  return {
    "--eap-primary": toHex(primary),
    "--eap-primary-rgb": toChannelList(primary),
    "--eap-primary-dark": toHex(mix(primary, BLACK, 0.18)),
    "--eap-primary-deep": toHex(deep),
    "--eap-primary-deep-rgb": toChannelList(deep),
    "--eap-primary-light": toHex(mix(primary, WHITE, 0.9)),
    "--eap-primary-border": toHex(mix(primary, WHITE, 0.65)),
    "--eap-accent": toHex(accent),
    "--eap-accent-rgb": toChannelList(accent),
    "--eap-on-primary": onPrimaryText(primary),
    // Primary as text on light surfaces falls back to a deeper shade when it can't reach contrast.
    "--eap-primary-text": toHex(
      contrastRatio(primary, WHITE) >= MINIMUM_TEXT_CONTRAST
        ? primary
        : readableDeep,
    ),
  };
};

// Dark surfaces: tints mix towards the surface rather than white, and text-coloured
// primary roles lighten until they reach contrast on it.
const deriveDarkTokens = (primary: Rgb): CtaTokens => {
  const [hue, saturation, lightness] = toHsl(primary);
  const accent = fromHsl([
    (hue + 12) % 360,
    saturation * 0.92,
    Math.min(lightness + 0.06, 0.82),
  ]);
  const deep = mix(primary, BLACK, 0.45);
  let readableText = primary;
  for (
    let whiteAmount = 0.15;
    contrastRatio(readableText, DARK_SURFACE) < MINIMUM_TEXT_CONTRAST &&
    whiteAmount <= 1;
    whiteAmount += 0.1
  ) {
    readableText = mix(primary, WHITE, whiteAmount);
  }
  return {
    "--eap-primary": toHex(primary),
    "--eap-primary-rgb": toChannelList(primary),
    "--eap-primary-dark": toHex(mix(primary, WHITE, 0.14)),
    "--eap-primary-deep": toHex(mix(primary, WHITE, 0.55)),
    "--eap-primary-deep-rgb": toChannelList(deep),
    "--eap-primary-light": toHex(mix(DARK_SURFACE, primary, 0.2)),
    "--eap-primary-border": toHex(mix(DARK_SURFACE, primary, 0.55)),
    "--eap-accent": toHex(accent),
    "--eap-accent-rgb": toChannelList(accent),
    "--eap-on-primary": onPrimaryText(primary),
    "--eap-primary-text": toHex(readableText),
  };
};

export const deriveCtaTokens = (primary: Rgb, mode: ColourMode): CtaTokens =>
  mode === "dark" ? deriveDarkTokens(primary) : deriveLightTokens(primary);

// Spectrum.Life's default CTA colour, used only when the active theme exposes none.
const DEFAULT_CTA_COLOUR: Rgb = [92, 71, 255];

export const resolveCtaColour = (
  candidates: Array<string | null | undefined>,
): Rgb => {
  for (const candidate of candidates) {
    const colour = parseColour(candidate);
    if (colour) return colour;
  }
  return DEFAULT_CTA_COLOUR;
};
